import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Search, SlidersHorizontal, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { getSecondaryColumns } from './columnConfiguration'
import { SecondaryAttributesFieldEditor } from './SecondaryAttributesFieldEditor'
import { SkuSearchCell } from './SkuSearchCell'
import type { SkuSaveData } from './SkuSearchCell'
import { VendorSearchCell } from './VendorSearchCell'
import { VENDOR_PICKER_COLUMN_IDS, vendorCellUpdates } from './vendorPicker'
import { formatDagValue, isCellEditable } from './DagCellRenderer'
import { evaluateFormula } from '@/lib/formulaEvaluator'
import { useDagTableStore } from '@/stores/useDagTableStore'
import { getRowHeaderLabel } from '../../utils/SecondaryAttributesDrawer.utils'
import type { SecondaryAttributesDrawerProps } from '../../types/secondary-attributes-drawer.types'
import type { DagColumnMeta } from '@/types/dag'

/* ------------------------------------------------------------------ */
/*  DrawerColumnPicker – local sub-component                          */
/* ------------------------------------------------------------------ */

interface DrawerColumnPickerProps {
  columns: DagColumnMeta[]
  hiddenColumnIds: Set<string>
  onToggle: (columnId: string) => void
  onSetColumnsHidden: (columnIds: string[], hidden: boolean) => void
}

function DrawerColumnPicker({
  columns,
  hiddenColumnIds,
  onToggle,
  onSetColumnsHidden,
}: DrawerColumnPickerProps) {
  const [search, setSearch] = useState('')
  const [activeGroup, setActiveGroup] = useState<string | null>(null)

  const groups = useMemo(() => {
    const seen = new Set<string>()
    for (const col of columns) {
      seen.add(col.group)
    }
    return [...seen]
  }, [columns])

  const filteredColumns = useMemo(() => {
    let result = columns
    if (activeGroup) {
      result = result.filter(c => c.group === activeGroup)
    }
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(c => c.label.toLowerCase().includes(q))
    }
    return result
  }, [columns, activeGroup, search])

  const groupedFiltered = useMemo(() => {
    const map = new Map<string, DagColumnMeta[]>()
    for (const col of filteredColumns) {
      const arr = map.get(col.group) ?? []
      arr.push(col)
      map.set(col.group, arr)
    }
    return map
  }, [filteredColumns])

  const filteredIds = useMemo(
    () => filteredColumns.map(c => c.id),
    [filteredColumns]
  )
  const visibleFilteredCount = filteredIds.filter(
    id => !hiddenColumnIds.has(id)
  ).length
  const allChecked =
    filteredIds.length > 0 && visibleFilteredCount === filteredIds.length
  const someChecked =
    visibleFilteredCount > 0 && visibleFilteredCount < filteredIds.length

  const handleSelectAll = (checked: boolean) => {
    onSetColumnsHidden(filteredIds, !checked)
  }

  const hiddenCount = [...hiddenColumnIds].filter(id =>
    columns.some(c => c.id === id)
  ).length

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0 cursor-pointer"
        >
          <SlidersHorizontal className="h-4 w-4" />
          {hiddenCount > 0 && (
            <Badge
              variant="secondary"
              className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 text-[10px] leading-none"
            >
              {hiddenCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        {/* Search */}
        <div className="p-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search columns..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        {/* Group filter pills */}
        <div className="flex gap-1.5 px-3 pb-2 overflow-x-auto">
          <Button
            variant="outline"
            size="sm"
            className={`cursor-pointer shrink-0 h-7 text-xs ${!activeGroup ? 'bg-primary text-white hover:bg-primary/90 hover:text-white' : ''}`}
            onClick={() => setActiveGroup(null)}
          >
            All
          </Button>
          {groups.map(group => (
            <Button
              key={group}
              variant="outline"
              size="sm"
              className={`cursor-pointer shrink-0 h-7 text-xs ${activeGroup === group ? 'bg-primary text-white hover:bg-primary/90 hover:text-white' : ''}`}
              onClick={() =>
                setActiveGroup(activeGroup === group ? null : group)
              }
            >
              {group}
            </Button>
          ))}
        </div>

        {/* Select all */}
        <div
          className="flex items-center gap-2 px-3 py-2 border-y border-border cursor-pointer select-none"
          onClick={() => handleSelectAll(!allChecked)}
        >
          <span className="pointer-events-none">
            <Checkbox
              checked={allChecked}
              {...(someChecked ? { 'data-state': 'indeterminate' } : {})}
            />
          </span>
          <span className="text-sm font-medium">Select all</span>
        </div>

        {/* Column list */}
        <div className="max-h-[360px] overflow-y-auto py-1">
          {groups
            .filter(g => groupedFiltered.has(g))
            .map(groupName => (
              <div key={groupName}>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {groupName}
                </div>
                {groupedFiltered.get(groupName)!.map(col => (
                  <div
                    key={col.id}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 cursor-pointer select-none"
                    onClick={() => onToggle(col.id)}
                  >
                    <span className="pointer-events-none">
                      <Checkbox checked={!hiddenColumnIds.has(col.id)} />
                    </span>
                    <span className="text-sm">{col.label}</span>
                  </div>
                ))}
              </div>
            ))}
          {filteredColumns.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
              No columns match your search
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/* ------------------------------------------------------------------ */
/*  SecondaryAttributesDrawer                                         */
/* ------------------------------------------------------------------ */

export function SecondaryAttributesDrawer({
  open,
  onOpenChange,
  row,
  columns,
  primaryAttributes,
  dropdownMap,
  onCellEdit,
  onMultiCellEdit,
  isReadOnly,
}: SecondaryAttributesDrawerProps) {
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null)
  const [focusedColumnId, setFocusedColumnId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<string>>(new Set())
  const computingCells = useDagTableStore(s => s.computingCells)

  const secondaryColumns = useMemo(
    () => getSecondaryColumns(columns, primaryAttributes),
    [columns, primaryAttributes]
  )

  // Stage 1: Remove columns hidden by the picker
  const visibleColumns = useMemo(
    () => secondaryColumns.filter(col => !hiddenColumnIds.has(col.id)),
    [secondaryColumns, hiddenColumnIds]
  )

  // Stage 2: Apply search text filter
  const displayedColumns = useMemo(() => {
    if (!search) return visibleColumns
    const q = search.toLowerCase()
    return visibleColumns.filter(col => col.label.toLowerCase().includes(q))
  }, [visibleColumns, search])

  const rowLabel = row ? getRowHeaderLabel(row, 0) : ''

  const handleToggleColumn = (columnId: string) => {
    setHiddenColumnIds(prev => {
      const next = new Set(prev)
      if (next.has(columnId)) next.delete(columnId)
      else next.add(columnId)
      return next
    })
  }
  const handleSetColumnsHidden = (columnIds: string[], hidden: boolean) => {
    setHiddenColumnIds(prev => {
      const next = new Set(prev)
      for (const id of columnIds) {
        if (hidden) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  const navigateToField = useCallback(
    (currentColumnId: string, forward: boolean) => {
      if (!row) return
      const currentIdx = displayedColumns.findIndex(
        c => c.id === currentColumnId
      )
      if (currentIdx === -1) {
        setEditingColumnId(null)
        return
      }

      const step = forward ? 1 : -1
      for (
        let i = currentIdx + step;
        i >= 0 && i < displayedColumns.length;
        i += step
      ) {
        const col = displayedColumns[i]
        if (!isReadOnly && isCellEditable(col, row.cells)) {
          setEditingColumnId(col.id)
          return
        }
      }
      // No more editable fields in this direction
      setEditingColumnId(null)
    },
    [displayedColumns, row, isReadOnly]
  )

  const navigateFocus = useCallback(
    (currentColumnId: string, forward: boolean) => {
      const currentIdx = displayedColumns.findIndex(
        c => c.id === currentColumnId
      )
      if (currentIdx === -1) {
        setFocusedColumnId(null)
        return
      }
      const nextIdx = forward ? currentIdx + 1 : currentIdx - 1
      if (nextIdx >= 0 && nextIdx < displayedColumns.length) {
        setFocusedColumnId(displayedColumns[nextIdx].id)
      } else {
        setFocusedColumnId(null)
      }
    },
    [displayedColumns]
  )

  const cellRefsMap = useRef<Map<string, HTMLDivElement>>(new Map())

  useEffect(() => {
    if (focusedColumnId) {
      const rafId = requestAnimationFrame(() => {
        cellRefsMap.current.get(focusedColumnId)?.focus()
      })
      return () => cancelAnimationFrame(rafId)
    }
  }, [focusedColumnId])

  const countText =
    displayedColumns.length === secondaryColumns.length
      ? `${secondaryColumns.length} secondary ${secondaryColumns.length === 1 ? 'attribute' : 'attributes'}`
      : `Showing ${displayedColumns.length} of ${secondaryColumns.length} secondary attributes`

  return (
    <Sheet
      open={open}
      onOpenChange={value => {
        if (!value) {
          setEditingColumnId(null)
          setFocusedColumnId(null)
        }
        if (value) {
          setSearch('')
          setHiddenColumnIds(new Set())
        }
        onOpenChange(value)
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex w-full flex-col sm:w-xl sm:max-w-xl"
        onPointerDownOutside={e => {
          const target = e.target
          if (
            target instanceof Node &&
            (target as Element).closest?.('[data-sku-dropdown]')
          ) {
            e.preventDefault()
          }
        }}
        onFocusOutside={e => {
          const target = e.target
          if (
            target instanceof Node &&
            (target as Element).closest?.('[data-sku-dropdown]')
          ) {
            e.preventDefault()
          }
        }}
        onInteractOutside={e => {
          const target = e.target
          if (
            target instanceof Node &&
            (target as Element).closest?.('[data-sku-dropdown]')
          ) {
            e.preventDefault()
          }
        }}
      >
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <SheetTitle className="text-lg font-semibold">
                Secondary Attributes
              </SheetTitle>
              {row && (
                <p className="mt-0.5 truncate text-sm text-muted-foreground">
                  {rowLabel}
                </p>
              )}
            </div>
            <SheetClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 cursor-pointer"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        {/* Toolbar: search + column picker */}
        {row && secondaryColumns.length > 0 && (
          <div className="flex items-center gap-2 border-b px-6 py-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search attributes..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
            <div className="relative">
              <DrawerColumnPicker
                columns={secondaryColumns}
                hiddenColumnIds={hiddenColumnIds}
                onToggle={handleToggleColumn}
                onSetColumnsHidden={handleSetColumnsHidden}
              />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!row ? (
            <p className="text-sm text-muted-foreground">No row selected.</p>
          ) : secondaryColumns.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No secondary attributes available for this row.
            </p>
          ) : displayedColumns.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No attributes match your search.
            </p>
          ) : (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground mb-3">{countText}</p>

              <div className="rounded-md border border-border">
                {displayedColumns.map((column, idx) => {
                  // Derived value computation (same as DagCellRenderer)
                  let cellValue = row.cells[column.id]
                  if (
                    column.kind === 'derived_local' &&
                    column.expr &&
                    (cellValue === null || cellValue === undefined)
                  ) {
                    cellValue = evaluateFormula(column.expr, row.cells)
                  }

                  // Editability check (same as DagCellRenderer)
                  const editable =
                    !isReadOnly && isCellEditable(column, row.cells)

                  const isEditing = editingColumnId === column.id
                  const isComputing = computingCells.has(
                    `${row.id}:${column.id}`
                  )

                  return (
                    <div
                      key={column.id}
                      className={`flex items-start gap-3 px-4 py-3 ${
                        idx < displayedColumns.length - 1
                          ? 'border-b border-border'
                          : ''
                      }`}
                    >
                      {/* Label */}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">
                          {column.label}
                        </p>
                      </div>

                      {/* Value */}
                      <div className="w-[180px] shrink-0">
                        {isComputing ? (
                          <div className="min-h-[2rem] flex items-center justify-center rounded px-2 bg-[#f3f4f6]">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          </div>
                        ) : isEditing &&
                          editable &&
                          column.id === 'like_sku_number' ? (
                          <SkuSearchCell
                            value={cellValue != null ? String(cellValue) : null}
                            isEditing={true}
                            allowNewSku={false}
                            onSave={(skuData: SkuSaveData) => {
                              onCellEdit(
                                row.id,
                                'like_sku_number',
                                skuData.skuNumber,
                                row.row_version
                              )
                              setEditingColumnId(null)
                              setFocusedColumnId(column.id)
                            }}
                            onCancelEdit={() => {
                              setEditingColumnId(null)
                              setFocusedColumnId(column.id)
                            }}
                            onTab={shiftKey =>
                              navigateToField(column.id, !shiftKey)
                            }
                          />
                        ) : isEditing &&
                          editable &&
                          VENDOR_PICKER_COLUMN_IDS.has(column.id) ? (
                          <VendorSearchCell
                            value={cellValue != null ? String(cellValue) : null}
                            isEditing={true}
                            onSave={vendor => {
                              if (onMultiCellEdit) {
                                onMultiCellEdit(
                                  row.id,
                                  vendorCellUpdates(vendor),
                                  row.row_version
                                )
                              } else {
                                onCellEdit(
                                  row.id,
                                  'vendor_id',
                                  vendor?.id ?? null,
                                  row.row_version
                                )
                              }
                              setEditingColumnId(null)
                              setFocusedColumnId(column.id)
                            }}
                            onCancelEdit={() => {
                              setEditingColumnId(null)
                              setFocusedColumnId(column.id)
                            }}
                            onTab={shiftKey =>
                              navigateToField(column.id, !shiftKey)
                            }
                          />
                        ) : isEditing && editable ? (
                          <div
                            onBlur={e => {
                              const container = e.currentTarget
                              // Delay to allow focus to settle — Radix Select
                              // portals its content outside this container
                              setTimeout(() => {
                                const active = document.activeElement
                                if (container.contains(active)) return
                                // Check if focus moved to a Radix portal
                                if (
                                  active?.closest(
                                    '[data-radix-popper-content-wrapper]'
                                  )
                                )
                                  return
                                setEditingColumnId(null)
                                setFocusedColumnId(column.id)
                              }, 150)
                            }}
                          >
                            <SecondaryAttributesFieldEditor
                              rowId={row.id}
                              rowVersion={row.row_version}
                              meta={column}
                              value={cellValue}
                              dropdownOptions={
                                column.dropdown_category
                                  ? (dropdownMap[column.dropdown_category] ??
                                    column.options?.map(o => ({ value: o })))
                                  : undefined
                              }
                              onCellEdit={(
                                rowId,
                                columnId,
                                value,
                                rowVersion
                              ) => {
                                onCellEdit(rowId, columnId, value, rowVersion)
                                setEditingColumnId(null)
                                setFocusedColumnId(column.id)
                              }}
                              onTab={shiftKey =>
                                navigateToField(column.id, !shiftKey)
                              }
                              onExitEdit={() => {
                                setEditingColumnId(null)
                                setFocusedColumnId(column.id)
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            ref={el => {
                              if (el) {
                                cellRefsMap.current.set(column.id, el)
                                if (
                                  focusedColumnId === column.id &&
                                  document.activeElement !== el
                                ) {
                                  requestAnimationFrame(() => el.focus())
                                }
                              } else {
                                cellRefsMap.current.delete(column.id)
                              }
                            }}
                            tabIndex={0}
                            className={`min-h-[2rem] flex items-center rounded px-2 outline-none ${
                              editable
                                ? 'cursor-pointer hover:bg-muted/50'
                                : 'bg-[#f3f4f6]'
                            } ${focusedColumnId === column.id ? 'ring-2 ring-ring' : ''}`}
                            onClick={() => setFocusedColumnId(column.id)}
                            onFocus={() => setFocusedColumnId(column.id)}
                            onBlur={() => {
                              if (focusedColumnId === column.id)
                                setFocusedColumnId(null)
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Tab') {
                                e.preventDefault()
                                navigateFocus(column.id, !e.shiftKey)
                              } else if (e.key === 'Enter' && editable) {
                                e.preventDefault()
                                setFocusedColumnId(null)
                                setEditingColumnId(column.id)
                              }
                            }}
                            onDoubleClick={
                              editable
                                ? () => {
                                    setFocusedColumnId(null)
                                    setEditingColumnId(column.id)
                                  }
                                : undefined
                            }
                          >
                            <span className="truncate text-sm">
                              {formatDagValue(cellValue, column.format)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
